import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { History, Clock, Trash2, RotateCcw } from 'lucide-react';
import type { TimesheetReportFilters } from '@/types/timesheetReport';
import { formatDistanceToNow } from 'date-fns';

export interface FilterHistoryEntry {
  id: string;
  filters: TimesheetReportFilters;
  appliedAt: Date;
  resultsCount?: number;
  label?: string;
}

interface FilterHistoryProps {
  history: FilterHistoryEntry[];
  onApplyHistory: (entry: FilterHistoryEntry) => void;
  onClearHistory: () => void;
  maxEntries?: number;
}

export const FilterHistory: React.FC<FilterHistoryProps> = ({
  history,
  onApplyHistory,
  onClearHistory,
  maxEntries = 10,
}) => {
  // Get a human-readable label for filter entry
  const getFilterLabel = (filters: TimesheetReportFilters): string => {
    const parts: string[] = [];

    // Date range
    if (filters.quickDateRange) {
      const labels: Record<string, string> = {
        'this-week': 'This Week',
        'last-week': 'Last Week',
        'this-month': 'This Month',
        'last-month': 'Last Month',
        'all-time': 'All Time',
      };
      parts.push(labels[filters.quickDateRange] || 'Custom Range');
    } else if (filters.dateFrom || filters.dateTo) {
      parts.push('Custom Range');
    }

    // Statuses
    if (filters.statuses && filters.statuses.length > 0) {
      if (filters.statuses.length === 1) {
        const statusLabels: Record<string, string> = {
          draft: 'Draft',
          submitted: 'Submitted',
          approved: 'Approved',
          rejected: 'Rejected',
          locked: 'Locked',
        };
        parts.push(statusLabels[filters.statuses[0]] || filters.statuses[0]);
      } else {
        parts.push(`${filters.statuses.length} Statuses`);
      }
    }

    // Employees
    if (filters.employeeIds && filters.employeeIds.length > 0) {
      parts.push(`${filters.employeeIds.length} Employee${filters.employeeIds.length > 1 ? 's' : ''}`);
    }

    // Departments
    if (filters.departments && filters.departments.length > 0) {
      parts.push(`${filters.departments.length} Dept${filters.departments.length > 1 ? 's' : ''}`);
    }

    // Projects
    if (filters.projectIds && filters.projectIds.length > 0) {
      parts.push(`${filters.projectIds.length} Project${filters.projectIds.length > 1 ? 's' : ''}`);
    }

    // Billable
    if (filters.billableOnly) {
      parts.push('Billable Only');
    }

    // Rejected
    if (filters.rejectedOnly) {
      parts.push('With Rejections');
    }

    return parts.length > 0 ? parts.join(' | ') : 'All Filters';
  };

  // Get active filter count
  const getFilterCount = (filters: TimesheetReportFilters): number => {
    let count = 0;
    if (filters.dateFrom || filters.dateTo) count++;
    if (filters.statuses && filters.statuses.length > 0) count++;
    if (filters.employeeIds && filters.employeeIds.length > 0) count++;
    if (filters.departments && filters.departments.length > 0) count++;
    if (filters.projectIds && filters.projectIds.length > 0) count++;
    if (filters.billableOnly) count++;
    if (filters.rejectedOnly) count++;
    return count;
  };

  // Limit history to max entries
  const displayHistory = history.slice(0, maxEntries);

  if (history.length === 0) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-gray-400" />
            <h3 className="text-sm font-medium text-gray-500">Filter History</h3>
          </div>
        </div>
        <div className="text-center py-8 text-sm text-gray-500">
          <History className="h-8 w-8 mx-auto mb-2 opacity-30" />
          <p>No filter history yet</p>
          <p className="text-xs mt-1">Your recent filters will appear here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-blue-600" />
          <h3 className="text-sm font-medium">Filter History</h3>
          <Badge variant="secondary" className="h-5 text-xs">
            {history.length}
          </Badge>
        </div>
        {history.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearHistory}
            className="h-7 text-xs gap-1 text-gray-500 hover:text-destructive"
          >
            <Trash2 className="h-3 w-3" />
            Clear
          </Button>
        )}
      </div>

      <ScrollArea className="h-[240px]">
        <div className="space-y-2 pr-4">
          {displayHistory.map((entry, index) => (
            <Button
              key={entry.id}
              variant="outline"
              className="w-full h-auto p-3 justify-start text-left hover:bg-gray-50"
              onClick={() => onApplyHistory(entry)}
            >
              <div className="flex items-start gap-3 w-full">
                <div className="flex-shrink-0 mt-0.5">
                  <RotateCcw className="h-4 w-4 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium truncate">
                      {entry.label || getFilterLabel(entry.filters)}
                    </span>
                    <Badge variant="secondary" className="h-5 text-xs flex-shrink-0">
                      {getFilterCount(entry.filters)}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Clock className="h-3 w-3" />
                    <span>
                      {formatDistanceToNow(entry.appliedAt, { addSuffix: true })}
                    </span>
                    {entry.resultsCount !== undefined && (
                      <>
                        <span>•</span>
                        <span>{entry.resultsCount} results</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Button>
          ))}
        </div>
      </ScrollArea>

      {history.length > maxEntries && (
        <div className="text-xs text-gray-500 text-center pt-1">
          Showing {maxEntries} of {history.length} recent filters
        </div>
      )}
    </div>
  );
};
